import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { FoodItem } from '../../types/nutrition';

interface FoodSearchProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (food: FoodItem) => void;
  filteredFoods: FoodItem[];
  className?: string;
}

export function FoodSearch({
  value,
  onChange,
  onSelect,
  filteredFoods,
  className = ''
}: FoodSearchProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search foods..."
          className="w-full bg-muted/50 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>

      {showDropdown && value && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-xl border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
          {filteredFoods.length > 0 ? (
            filteredFoods.map((food) => (
              <button
                key={food.id}
                onClick={() => {
                  onSelect?.(food);
                  setShowDropdown(false);
                }}
                className="w-full p-3 hover:bg-muted/10 flex items-center justify-between text-left transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span>{food.emoji}</span>
                  <div>
                    <div className="font-medium">{food.name}</div>
                    {food.serving && (
                      <div className="text-xs text-muted-foreground">{food.serving}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">{food.calories} cal</div>
                  {food.protein && food.carbs && food.fat && (
                    <div className="text-xs text-muted-foreground">
                      P: {food.protein}g • C: {food.carbs}g • F: {food.fat}g
                    </div>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              No foods found. Try a different search term.
            </div>
          )}
        </div>
      )}
    </div>
  );
}